import { inject } from "@vercel/analytics";
import { round, prefs, beginRound } from "./state.js";
import { MAX_TRIES, hintFor } from "./hint.js";
import { fetchItem, fetchImages } from "./api.js";
import { render, showSkeleton, showError } from "./render.js";
import { openModal, initModal, onNextItem } from "./modal.js";
import { initIntro } from "./intro.js";
import { initCategoryPicker } from "./category.js";
import { recordResult } from "./supabase.js";
import { pushHistory, initHistory } from "./history.js";

// --- Prefetch buffer: the next item preloaded in the background ---
let prefetched = null;        // { category, item, images }
let prefetchInflight = null;  // Promise currently populating `prefetched`

function invalidatePrefetch() {
  prefetched = null;
  prefetchInflight = null;
}

async function prefetchNext() {
  if (prefetched || prefetchInflight) return;
  const cat = prefs.category;
  prefetchInflight = (async () => {
    try {
      const item = await fetchItem(cat);
      // kick off image fetch + tell the browser to warm the image cache
      const preload = document.createElement("link");
      preload.rel = "preload"; preload.as = "image"; preload.href = item.imgUrl;
      preload.referrerPolicy = "no-referrer";
      document.head.appendChild(preload);
      const images = await fetchImages(item.id);
      for (const url of images.slice(0, 4)) {
        const l = document.createElement("link");
        l.rel = "preload"; l.as = "image"; l.href = url;
        l.referrerPolicy = "no-referrer";
        document.head.appendChild(l);
      }
      // Only accept it if the user hasn't changed category in the meantime
      if (prefs.category === cat) prefetched = { category: cat, item, images };
    } catch {} finally {
      prefetchInflight = null;
    }
  })();
}

function handleGuess(raw) {
  const guess = parseFloat(raw);
  if (!isFinite(guess) || guess < 0) return;
  const { item } = round;
  const rounded = Math.round(guess * 100) / 100;
  const actual = Math.round(item.amount * 100) / 100;
  const tolerance = Math.max(1, actual * 0.03);
  const won = Math.abs(rounded - actual) <= tolerance + 1e-9;
  const hint = won ? { cls: "correct", symbol: "✓", text: "Exact", tier: "exact" } : hintFor(rounded, actual);
  round.guesses.push({ guess: rounded, hint });

  if (won) {
    round.done = true;
    round.won = true;
  } else if (round.guesses.length >= MAX_TRIES) {
    round.done = true;
    round.won = false;
  }
  render(handleGuess);
  if (round.done) {
    pushHistory(round.item, round.won, round.guesses);
    recordResult(round.item.id, round.won).then(stats => {
      round.stats = stats;
      render(handleGuess);
      setTimeout(openModal, 280);
    });
  }
}

async function loadNew() {
  // Use the prefetched item if it matches the current category
  if (prefetched && prefetched.category === prefs.category) {
    const { item, images } = prefetched;
    prefetched = null;
    beginRound(item);
    if (images && images.length) round.images = images;
    render(handleGuess);
    setTimeout(prefetchNext, 50);
    return;
  }

  showSkeleton();
  try {
    const item = await fetchItem(prefs.category);
    beginRound(item);
    render(handleGuess);
    fetchImages(item.id).then(images => {
      if (round.item && round.item.id === item.id && images.length) {
        round.images = images;
        render(handleGuess);
      }
    });
    setTimeout(prefetchNext, 300);
  } catch (e) {
    showError(e.message);
  }
}

inject();
initIntro();
initModal();
initHistory();
onNextItem(loadNew);
initCategoryPicker(() => { invalidatePrefetch(); loadNew(); });
loadNew();
